import React from "react";
import { TextButton } from "../../button/TextButton";
import { makeStyles } from "@mui/styles";
import { Box, IconButton, Theme, useTheme } from "@mui/material";
import clsx from "clsx";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { JourneyAnchors } from "../../../constants";

interface StylesProps {
  separateArrows?: boolean;
}

const useStyles = makeStyles<Theme, StylesProps>((theme) => ({
  container: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    width: ({ separateArrows }) => separateArrows && "100%",
  },
  button: {
    backgroundColor: "transparent",
    borderWidth: 0,
    color: theme.palette.text.secondary,
    borderRadius: 4,
    fontSize: 14,
    fontWeight: 700,
    margin: 0,
    padding: theme.spacing(0.8, 1),
    lineHeight: "17px",
    minWidth: 0,
    "&:hover": {
      color: theme.palette.secondary.contrastText,
      backgroundColor: theme.palette.secondary.main,
    },
  },
  activeButton: {
    color: theme.palette.secondary.contrastText,
    backgroundColor: theme.palette.secondary.main,
  },
  navButtonRight: {
    transform: "rotate(180deg)",
  },
  navButton: {
    width: 40,
  },
  invisible: {
    visibility: "hidden",
  },
  headerNav: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    width: "100%",
    justifyContent: ({ separateArrows }) => separateArrows && "flex-end",
  },
}));

export enum TimeRangeUnit {
  MONTH = "month",
  QUARTER = "quarter",
  YEAR = "year",
  ALL_TIME = "all_time",
}
const TimeRangeLabel = (unit: TimeRangeUnit) => {
  const labels: Record<TimeRangeUnit, string> = {
    [TimeRangeUnit.MONTH]: "Month",
    [TimeRangeUnit.QUARTER]: "Quarter",
    [TimeRangeUnit.YEAR]: "Year",
    [TimeRangeUnit.ALL_TIME]: "All",
  };
  return labels[unit];
};

const MONTHS_IN_YEAR = 12;
const MONTHS_IN_QUARTER = 3;

// Calculate new nav index when switching time range units
const calculateNewNavIndex = (
  currentUnit: TimeRangeUnit,
  newUnit: TimeRangeUnit,
  currentNavIndex: number,
): number => {
  if (currentUnit === newUnit) return currentNavIndex;

  // map time range units to their equivalent in months
  const unitToMonths: Record<TimeRangeUnit, number> = {
    [TimeRangeUnit.MONTH]: 1,
    [TimeRangeUnit.QUARTER]: MONTHS_IN_QUARTER,
    [TimeRangeUnit.YEAR]: MONTHS_IN_YEAR,
    [TimeRangeUnit.ALL_TIME]: 0,
  };

  const monthsFromCurrent = currentNavIndex * unitToMonths[currentUnit];

  if (newUnit === TimeRangeUnit.ALL_TIME) {
    return 0;
  }

  const newNavIndex = Math.round(monthsFromCurrent / unitToMonths[newUnit]);
  return newNavIndex;
};

interface ITimeRangeButtonsProps {
  activeButton: TimeRangeUnit;
  setActiveButton: (unit: TimeRangeUnit) => void;
  navIndex: number;
  setNavIndex: (index: number) => void;
  disablePrev?: boolean;
  disableNext?: boolean;
  // move arrows to far right
  separateArrows?: boolean;
}

const TimeRangeButtons = (props: ITimeRangeButtonsProps) => {
  const {
    activeButton,
    setActiveButton,
    navIndex,
    setNavIndex,
    disablePrev,
    disableNext,
    separateArrows,
  } = props;

  const s = useStyles({ separateArrows });
  const theme = useTheme();

  const disableNav = activeButton === TimeRangeUnit.ALL_TIME;

  const handleUnitChange = (newUnit: TimeRangeUnit) => {
    if (newUnit !== activeButton) {
      const newNavIndex = calculateNewNavIndex(activeButton, newUnit, navIndex);
      setActiveButton(newUnit);
      setNavIndex(newNavIndex);
    }
  };

  return (
    <Box className={clsx(s.container, JourneyAnchors.DATE_RANGE_FILTER)}>
      {(Object.values(TimeRangeUnit) as TimeRangeUnit[]).map((unit) => (
        <TextButton
          key={unit}
          className={clsx(s.button, activeButton === unit && s.activeButton)}
          onClick={() => handleUnitChange(unit)}
        >
          {TimeRangeLabel(unit)}
        </TextButton>
      ))}
      <Box className={clsx(s.headerNav)}>
        <IconButton
          className={s.navButton}
          onClick={() => setNavIndex(navIndex - 1)}
          disabled={disablePrev || disableNav}
        >
          <ChevronLeft
            scale={2}
            color={
              disablePrev || disableNav
                ? theme.palette.text.disabled
                : theme.palette.text.primary
            }
          />
        </IconButton>
        <IconButton
          className={clsx(s.navButton)}
          onClick={() => setNavIndex(navIndex + 1)}
          disabled={disableNext || disableNav}
        >
          <ChevronRight
            scale={2}
            color={
              disableNext || disableNav
                ? theme.palette.text.disabled
                : theme.palette.text.primary
            }
          />
        </IconButton>
      </Box>
    </Box>
  );
};

export default TimeRangeButtons;
